/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { Component, ContentChild, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';
import { uniqueId } from 'lodash-es';
import { DialogHeaderComponent } from './dialog-header/dialog-header.component';

const dialogVariants = cva('[&:open]:flex flex-col w-full overflow-hidden rounded-lg bg-background-50 p-0 shadow-lg border-0 text-left', {
  variants: {
    size: {
      small: 'm-auto max-w-[min(36rem,calc(100vw-2rem))] max-h-[calc(100vh-2rem)]',
      medium: 'm-auto max-w-[min(56rem,calc(100vw-2rem))] max-h-[calc(100vh-2rem)]',
      full: 'm-0 h-full max-w-full max-h-full rounded-none',
    },
  },
  defaultVariants: { size: 'small' },
});

type DialogVariants = VariantProps<typeof dialogVariants>;

@Component({
  selector: 'ods-dialog',
  imports: [CdkTrapFocus, NgClass],
  template: `
    <dialog
      #dialog
      [ngClass]="dialogVariants({ size })"
      [attr.aria-labelledby]="headingId"
      aria-modal="true"
      data-test-id="dialog"
      (click)="onBackdropClick($event)"
      (cancel)="onCancel($event)"
      (close)="onNativeClose()"
    >
      <div
        #dialogInner
        tabindex="-1"
        class="flex min-h-0 flex-1 flex-col gap-6 p-8 outline-none"
        cdkTrapFocus
        data-test-id="dialog-inner"
      >
        <ng-content />
      </div>
    </dialog>
  `,
})
export class DialogComponent implements OnDestroy {
  private readonly scrollStrategy = inject(ScrollStrategyOptions).block();

  @Input() size: DialogVariants['size'] = 'small';
  @Input() closable: boolean = true;

  @ViewChild('dialog') private readonly dialogRef!: ElementRef<HTMLDialogElement>;
  @ViewChild('dialogInner') private readonly dialogInnerRef!: ElementRef<HTMLDivElement>;

  @ContentChild(DialogHeaderComponent) set headerComponent(header: DialogHeaderComponent | undefined) {
    if (header) header.headingId = this.headingId;
  }

  @Output() closed = new EventEmitter<void>();

  readonly headingId: string = uniqueId('dialog-heading-');
  readonly dialogVariants = dialogVariants;

  open(): void {
    this.scrollStrategy.enable();
    this.dialogRef.nativeElement.showModal();
    this.dialogInnerRef.nativeElement.focus();
  }

  close(): void {
    this.dialogRef.nativeElement.close();
  }

  onBackdropClick(event: MouseEvent): void {
    if (this.closable && event.target === this.dialogRef.nativeElement) this.close();
  }

  onCancel(event: Event): void {
    event.preventDefault();
    if (this.closable) this.close();
  }

  onNativeClose(): void {
    this.scrollStrategy.disable();
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.scrollStrategy.disable();
  }
}
